import { Divider, Grid, IconButton, Tooltip, styled } from "@mui/material";
import theme from "Theme/AppTheme";
import { observer } from "mobx-react";
import React, { Suspense, useEffect, useState } from "react";
import { useStore } from "utils/useStore";
import { AgentLogoWithNameFull } from "./Branding/AgentLogoWithNameFull";
import AcxButton from "components/UI/AcxButton";
import { Add, OpenInFull, Settings } from "@mui/icons-material";
import { AgentConversation } from "./Conversation/AgentConversation";
import { useOpenCloseFullscreenAgent } from "./AgentUtils";
import CloseIcon from "@mui/icons-material/Close";
import AgentStore from "./Stores/AgentStore";
import { AuthStore } from "stores/AuthStore";
import { useCurrentChatStore } from "./Stores/useCurrentChat";
import {
    preloadSessionSettingsDialog,
    SessionSettingsDialog,
} from "./Conversation/SessionSettingsDialog.lazy";
import { hasKboSettingsOverrides } from "./Models/KboSettingsHelpers";
import AcxChip from "components/UI/AcxChip";
import SparklingSvg from "SvgIcons/SparklingSvg";

type Props = {
    close: () => void;
};

export const NewChatButton = styled(AcxButton)(({ theme }) => ({
    backgroundColor: "transparent",
    color: theme.palette.black.main,
    whiteSpace: "nowrap",
    flexShrink: 0,
    ":hover": {
        backgroundColor: "transparent",
        opacity: "75%",
    },
}));

const AgentChat: React.FC<Props> = observer(({ close }) => {
    const agentStore = useStore(AgentStore);
    const authStore = useStore(AuthStore);
    const currentChatStore = useCurrentChatStore();
    const { openFullscreenAgent } = useOpenCloseFullscreenAgent();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const hasSessionSettingsOverrides = hasKboSettingsOverrides(
        currentChatStore.sessionSettings,
        agentStore.defaultSessionSettings,
    );

    const isUltra = authStore.isUserUltra();

    useEffect(() => {
        if (!isUltra) return;

        const idleWindow = window as Window & {
            requestIdleCallback?: (
                callback: () => void,
                options?: { timeout?: number },
            ) => number;
            cancelIdleCallback?: (handle: number) => void;
        };

        if (idleWindow.requestIdleCallback) {
            const handle = idleWindow.requestIdleCallback(
                preloadSessionSettingsDialog,
                { timeout: 3000 },
            );
            return () => idleWindow.cancelIdleCallback?.(handle);
        }

        const handle = window.setTimeout(preloadSessionSettingsDialog, 750);
        return () => window.clearTimeout(handle);
    }, [isUltra]);

    return (
        <Grid
            container
            direction="column"
            flexWrap="nowrap"
            height="100%"
            width="calc(max(20dvw, 372px))"
        >
            <Grid
                container
                item
                alignItems="center"
                justifyContent="space-between"
                flexWrap="nowrap"
                paddingBlock={1}
                paddingInline={2}
            >
                <Grid
                    container
                    item
                    alignItems="center"
                    columnGap={1}
                    flexWrap="nowrap"
                    sx={{ width: "fit-content", cursor: "pointer" }}
                    onClick={() => {
                        openFullscreenAgent();
                    }}
                    id="kbo-logo-open-fullscreen"
                >
                    <AgentLogoWithNameFull width="71" height="24" />
                    {agentStore.showKboUpdateAnnouncement && (
                        <AcxChip
                            size="small"
                            color="brandBlend"
                            label="Updated"
                            id="kbo-updated-tag"
                            customMaterialIcon={
                                <SparklingSvg
                                    gradient={false}
                                    width={12}
                                    height={12}
                                />
                            }
                            sx={{
                                marginBottom: 0,
                                flexShrink: 0,
                                "& .MuiChip-icon": { color: "white.main" },
                            }}
                        />
                    )}
                </Grid>
                <Grid
                    container
                    item
                    alignItems="center"
                    columnGap={0.5}
                    flexWrap="nowrap"
                    sx={{ width: "fit-content", flexShrink: 0 }}
                >
                    <Grid item>
                        <Tooltip title="New chat">
                            <IconButton
                                onClick={() => {
                                    if (agentStore.shouldPaywallOnInteract) {
                                        agentStore.openPaywallDialog();
                                        return;
                                    }
                                    agentStore.newChat();
                                }}
                                size="small"
                                id="kbo-new-chat"
                                aria-label="New chat"
                            >
                                <Add
                                    fontSize="small"
                                    style={{ color: theme.palette.gray[400] }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    {isUltra && (
                        <Grid item>
                            <Tooltip title="Session settings">
                                <IconButton
                                    onMouseEnter={preloadSessionSettingsDialog}
                                    onFocus={preloadSessionSettingsDialog}
                                    onClick={() => {
                                        preloadSessionSettingsDialog();
                                        setSettingsOpen(true);
                                    }}
                                    size="small"
                                    id="kbo-session-settings"
                                    aria-label="Session settings"
                                >
                                    <Settings
                                        fontSize="small"
                                        sx={{
                                            color: hasSessionSettingsOverrides
                                                ? theme.palette.primary.main
                                                : theme.palette.gray[400],
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                    <Grid item>
                        <Tooltip title="Expand">
                            <IconButton
                                onClick={() => {
                                    openFullscreenAgent();
                                }}
                                size="small"
                                id="kbo-view-history"
                                aria-label="Expand"
                            >
                                <OpenInFull
                                    fontSize="small"
                                    style={{ color: theme.palette.gray[400] }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <IconButton
                            onClick={() => close()}
                            size="small"
                            id="kbo-close-button"
                        >
                            <CloseIcon
                                fontSize="small"
                                style={{ color: theme.palette.gray[400] }}
                            />
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <Divider />
            </Grid>
            <AgentConversation />
            {isUltra && settingsOpen && (
                <Suspense fallback={null}>
                    <SessionSettingsDialog
                        open={settingsOpen}
                        onClose={() => setSettingsOpen(false)}
                    />
                </Suspense>
            )}
        </Grid>
    );
});

export default AgentChat;
